import React, { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon.js";

/** A button that opens a small list of actions under it; closes on a click elsewhere or Escape. */
export const Menu: React.FC<{ label?: React.ReactNode; icon?: string; className?: string; title?: string; align?: "left" | "right"; children: React.ReactNode | ((close: () => void) => React.ReactNode); disabled?: boolean; /** the list floats over the page (for a bar that clips its overflow, like the timeline's tools) */ float?: boolean }> = ({ label, icon, className = "btn sm quiet", title, align = "right", children, disabled, float }) => {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [at, setAt] = useState<{ left?: number; right?: number; top?: number; bottom?: number } | null>(null);
  // a floating list is placed from the button's own rectangle, so a clipping bar cannot cut it off
  useEffect(() => {
    if (!open || !float || !ref.current) { setAt(null); return; }
    const r = ref.current.getBoundingClientRect();
    setAt(align === "left" ? { left: r.left, top: r.bottom + 6 } : { right: window.innerWidth - r.right, top: r.bottom + 6 });
  }, [open, float, align]);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // near the bottom of the screen (the phone's transport, a row low in a list) the list opens upwards, so it can be reached
  useEffect(() => {
    if (!open) { setUp(false); return; }
    const el = listRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.bottom > window.innerHeight - 8 && r.top - r.height - 12 > 0) { setUp(true); if (float && ref.current) { const b = ref.current.getBoundingClientRect(); setAt((a) => ({ ...(a ?? {}), top: undefined, bottom: window.innerHeight - b.top + 6 })); } }
  }, [open, at?.top]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", close); window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", key); };
  }, [open]);
  const close = () => setOpen(false);
  return (
    <div ref={ref} className="menuwrap">
      <button className={className} onClick={() => setOpen(!open)} title={title} disabled={disabled} aria-haspopup="menu" aria-expanded={open}>
        {icon ? <Icon name={icon} size={14} /> : null}{label ?? "···"}{label && !icon ? <Icon name="chev-down" size={13} /> : null}
      </button>
      {open ? <div ref={listRef} className={`menu${align === "left" ? " left" : ""}${up ? " up" : ""}${float ? " float" : ""}`} style={float && at ? { position: "fixed", left: at.left, right: at.right, top: at.top, bottom: at.bottom } : undefined} role="menu" onClick={(e) => { const t = e.target as HTMLElement; if (t.closest("button, a") && !t.closest("[data-keep]")) close(); }}>{typeof children === "function" ? children(close) : children}</div> : null}
    </div>
  );
};
